import { Implant, SkipImplantCheckInReason, SkipImplantCheckOutReason } from './Implants';
import { OrderStatus } from './Orders';
import { PageableRequest, PageableResponse } from './Pageable';
import { KitInstanceId, Set } from './Sets';
import { SortOrder } from './Sorting';
import { Surgery } from './Surgery';
import { GetTrayCapturePrintOutRequestBody } from './Trays';

export enum DeliveryContent {
  Default = 0,
  Instruments = 1,
  Implants = 2,
  InstrumentsAndImplants = 3,
}

export enum FilterStatus {
  ACTION_REQUIRED = 'Action required',
  MERGED = 'Merged',
  PENDING = 'Pending',
  DRAFT = 'Draft',
  SAVED = 'Saved',
  DELETED = 'Deleted',
  FAILED = 'Failed',
}

export enum DeliveryContentType {
  DMInstrumentSets = 'Instrument Sets',
  SterileImplants = 'Sterile Implants',
}

export enum FileStatus {
  DRAFT_INITIALIZED = 'DRAFT_INITIALIZED',
  DRAFT_FAILED = 'DRAFT_FAILED',
  DRAFT_COMPLETED = 'DRAFT_COMPLETED',
  DRAFT = 'DRAFT',
  MERGE_STARTED = 'MERGE_STARTED',
  MERGE_FAILED = 'MERGE_FAILED',
  MERGED = 'MERGED',
  DELETED = 'DELETED',
  SAVED = 'SAVED',
}

// high-level delivery lifcycle stages, bascally: not used, in use, done with
export type DeliveryInspectionStage =
  | 'OUTBOUND' // not used
  | 'CHECK_IN' // in use
  | 'CHECK_OUT'; // done with

export type TrayInspectionStage = DeliveryInspectionStage | 'INBOUND' | 'INSPECTION' | 'RECEIVE';
export type ImplantInspectionStage = TrayInspectionStage;

// detailed delivery lifecycle stages, used for tracking the delivery chain of custody
export type DeliveryStatus =
  | 'ORDERED'
  | 'DISPATCHED'
  | 'OUTBOUND'
  | 'DELIVERED'
  | 'RECEIVED'
  | 'PARTIALLY_CHECKED_IN'
  | 'CHECKED_IN'
  | 'PARTIALLY_CHECKED_OUT'
  | 'CHECKED_OUT'
  | 'RETURNED'
  | 'DELETED'
  | 'CANCELED'
  | 'OVERDUE_FOR_CHECK_IN'
  | 'OVERDUE_FOR_CHECK_OUT'
  | 'NOT_DELIVERED';

export const DELIVERY_FOR_CHECK_IN_STATUSES: DeliveryStatus[] = [
  'DISPATCHED',
  'OUTBOUND',
  'RECEIVED',
  'PARTIALLY_CHECKED_IN',
  'OVERDUE_FOR_CHECK_IN',
];

export type DeliveryStageStatus = 'ONGOING' | 'COMPLETED';

export type DeliveryPriority = 1 | 2 | 3;

export enum DeliveriesSortField {
  Supplier = 'supplier',
  DeliveryNumber = 'deliveryNumber',
  Status = 'status',
  ClinicalArea = 'clinicalArea',
  Surgeon = 'surgeon',
  SurgeryDate = 'surgeryDate',
  DueDate = 'dueDate',
  ShipmentId = 'shipmentId',
  Eta = 'eta',
}

export type DeliveryId = number;

export type DeliverySummary = {
  supplier: string;
  clinicalArea: string;
  surgeryDate: string | Date;
  dueDate: string | Date;
  deliveryId: DeliveryId;
  orderNumber: string;
  orderStage: OrderStatus;
  deliveryNumber: string;
  surgeryReference: string;
  status: DeliveryStatus;
  patientInitials: string;
  express: boolean;
  turnaroundOrigin?: boolean;
  turnaroundDestination?: boolean;
  priority?: DeliveryPriority;
  surgeon?: string | null;
  content: DeliveryContent;
  supplierTenantId: string;
  supplierCreated?: boolean;
  shipmentId?: string;
  etaInfo?: EtaInfo;
  selectedRate?: SelectedRate;
};

export const makeDeliverySummary = (data: Partial<DeliverySummary>): DeliverySummary => ({
  supplier: '',
  clinicalArea: '',
  surgeryDate: '',
  dueDate: '',
  deliveryId: 0,
  orderNumber: '',
  orderStage: 'Default',
  deliveryNumber: '',
  surgeryReference: '',
  patientInitials: '',
  status: 'RECEIVED',
  express: false,
  turnaroundOrigin: false,
  turnaroundDestination: false,
  priority: 1,
  surgeon: '',
  content: DeliveryContent.Default,
  supplierTenantId: '',
  supplierCreated: false,
  ...data,
});

export type ShipperDetails = {
  // numRoadCases is the single road case count, labelled "No. of Single Roadcase" in the UI
  numRoadCases: number;
  numTubs: number;
  numBoxes: number;
  numPelicanCases: number;
  numDoubleRoadCases: number;
};

export type UpdatedDelivery = {
  dueDate: string;
  priority: DeliveryPriority;
  supplierTenantId: string;
  deviceRepId?: string;
  surgeon?: string | null;
  surgeryDate: string;
  clinicalArea: string;
  surgeryReference: string;
  content: DeliveryContent;
  deliveryNumber: string;
  orderNumber: string;
  patientInitials?: string;
  receivedAt?: string;
  shipperDetails?: ShipperDetails;
  shipper?: string;
  shipmentId?: string;
  colourCode?: string | null;
};

export type DeliveryReturnDetailsParams = {
  deliveryId: string | number;
  returnedByRepId?: string;
  returnDetails?: ShipperDetails;
};

export type NotifySetCheckedOutParams = DeliveryReturnDetailsParams & {
  kitInstanceId: string;
};

export type TrayLabelInfo = {
  setName: string;
  trayNumber: number;
  assetNumber?: string;
  id?: string;
};

export type PrintLabelRequestBody = {
  trayLabelInfos: TrayLabelInfo[];
  patientInitials?: string;
};

export type DeliveryImplantInspection = {
  CHECK_IN: {
    skippedReason: SkipImplantCheckInReason;
    gsImagePath: null;
    count: number;
    date: string;
    updatedAt: string;
    updatedBy: string;
  } | null;
  CHECK_OUT: {
    skippedReason: SkipImplantCheckOutReason;
    gsImagePath: null;
    count: number;
    date: string;
    updatedAt: string;
    updatedBy: string;
  } | null;
};

export type SelectedRate = {
  id: string;
  name: string;
  priority: number;
};

export type Signature = {
  name?: string;
  timestamp?: string;
};

export type EtaInfo = {
  orderEta?: string;
  actualEta?: string;
};

export type Delivery = {
  id?: number;
  orderRecordId?: string;
  supplier: {
    name: string;
    tenantId: string;
    deviceRepId?: string;
  };
  orderNumber: string;
  patientInitials?: string;
  deliveryNumber: string;
  deliveryNumberAutoGenerated?: boolean;
  status: DeliveryStatus;
  priority?: DeliveryPriority;
  surgery: Surgery;
  numTraysReceived: number;
  numTraysSentSterilising: number;
  content: DeliveryContent;
  shipper?: string;
  shipmentId?: string;
  itemCount?: number;
  etaInfo?: EtaInfo;
  shipperDetails?: ShipperDetails;
  receivedDetails?: {
    receivedBy: string;
    receivedAt: string | Date;
  };
  sets: Set[];
  implants?: Implant[];
  dueDate: string;
  supplierSideCreated: boolean;
  colourCode?: string | null;
  implantInspection?: DeliveryImplantInspection;
  selectedRate?: SelectedRate;
  signature?: Signature;
};

export const isDeliveryForCheckIn = (status: DeliveryStatus) => DELIVERY_FOR_CHECK_IN_STATUSES.includes(status);

export const makeDelivery = (data: Partial<Delivery>): Delivery => ({
  supplier: {
    name: '',
    tenantId: '',
  },
  orderNumber: '',
  deliveryNumber: '',
  status: 'RECEIVED',
  surgery: {
    clinicalArea: '',
    surgeryDate: '',
    surgeryReference: '',
    surgeonId: '',
    surgeonName: '',
  },
  numTraysReceived: 0,
  colourCode: null,
  numTraysSentSterilising: 0,
  content: DeliveryContent.Default,
  shipperDetails: {
    numRoadCases: 0,
    numTubs: 0,
    numBoxes: 0,
    numPelicanCases: 0,
    numDoubleRoadCases: 0,
  },
  receivedDetails: {
    receivedBy: '',
    receivedAt: '',
  },
  sets: [],
  dueDate: '',
  supplierSideCreated: false,
  orderRecordId: '',
  ...data,
});

export type DeliveriesResponse = PageableResponse<DeliverySummary>;
export type DeliveryBody = {
  receivedAt: string;
  supplierTenantId: string;
  deviceRepId?: string;
  orderRecordId?: string;
  orderNumber: string;
  deliveryNumber: string;
  surgeonId?: string | null;
  surgeryDate: string;
  patientInitials?: string;
  dueDate: string;
  content: DeliveryContent;
  surgeryReference: string;
  shipperDetails: ShipperDetails;
  shipper?: string;
  shipmentId?: string;
  bypassedConnectedSupplier?: boolean;
  createNewOrder?: boolean;
  clinicalArea: string;
  colourCode?: string | null;
};

export const makeDeliveryBody = (data: Partial<DeliveryBody>): DeliveryBody => ({
  receivedAt: '',
  supplierTenantId: '',
  orderNumber: '',
  deliveryNumber: '',
  surgeonId: '',
  surgeryDate: '',
  patientInitials: '',
  dueDate: '',
  content: DeliveryContent.Default,
  surgeryReference: '',
  shipperDetails: {
    numRoadCases: 0,
    numTubs: 0,
    numBoxes: 0,
    numPelicanCases: 0,
    numDoubleRoadCases: 0,
  },
  clinicalArea: '',
  ...data,
});

export type DeliveriesReponse = PageableResponse<DeliverySummary>;

export type LabeledFilter = {
  value: string;
  label: string;
  id?: string;
};

export enum HistoricalSwitch {
  All = '-1',
  Active = 'false',
  Inactive = 'true',
}

export type DeliveriesSortAlgorithm = 'critical_items' | 'awaiting_check_out' | 'awaiting_check_in';

export type DeliveriesFilters = Partial<{
  search: string;
  lower_surgery_date: string;
  upper_surgery_date: string;
  surgery_date: string;
  due_date: string;
  supplier: LabeledFilter;
  surgeon: string;
  express: string;
  search_exact: 'true' | 'false';
  search_order_number?: string;
  search_delivery_number?: string;
  search_surgery_reference?: string;
  status: DeliveryStatus | DeliveryStatus[];
  sort_order: SortOrder;
  sort_by: DeliveriesSortField;
  sort_algorithm: DeliveriesSortAlgorithm;
  historical: HistoricalSwitch;
  surgery_reference?: string;
}>;

export type DeliveriesRequestFilters = Partial<PageableRequest & DeliveriesFilters>;

export type DeliveryResponse = {
  deliveryId: DeliveryId;
  status: string; // CREATE_SUCCESS
};

export type SubmitOrderInspectionRequest = {
  stage: TrayInspectionStage;
  status: DeliveryStageStatus;
  shipperDetails?: ShipperDetails;
};

export type SubmitOrderInspectionResponse = {
  deliveryStatus: DeliveryStatus;
};

export enum DeliveryQuickCheckOutReason {
  WithoutSaviCapture = 'withoutSaviCapture',
  SalesRep = 'salesRep',
  LimitedTime = 'limitedTime',
  CancelledSurgery = 'cancelledSurgery',
  NoReturnRequired = 'noReturnRequired',
}

export type DeliveryQuickCheckOutRequest = {
  reason: DeliveryQuickCheckOutReason;
  date: string;
  quickCheckedOutBy: string | null;
};

export type DeliveryReceiveDispatchRequest = {
  status: DeliveryStageStatus;
  stage: TrayInspectionStage; // not correct but the 'RECEIVE' alias
};

export type DeliveryReceiveDispatchResponse = {
  status: DeliveryStageStatus;
  stage: TrayInspectionStage; // not correct but the 'RECEIVE' alias
  delivery_id: number;
};

export type DeliveryQuickCheckOutResponse = DeliveryQuickCheckOutRequest;

export type AddTurnaroundSetToDeliveryRequest = {
  kit_instance_id: KitInstanceId;
};

export type AddAllTurnaroundsToDeliveryRequest = {
  kitInstanceIds?: KitInstanceId[];
  originImplantDeliveryId?: number; // the delivery the turnaround implants originate from
};

export type GetTrayCapturePrintOutParams = {
  deliveryId: DeliveryId;
  trayId: string;
  tray: GetTrayCapturePrintOutRequestBody;
};

export type ValidateSurgeryReferenceRequest = {
  supplierTenantId: string;
  surgeonId: string;
  surgeryDate: string;
  surgeryReference: string;
  requestId?: number;
  patientInitials?: string;
};

export type ValidateSurgeryReferenceResponse = {
  isValid: boolean;
  requestId?: number;
};
